'use client';

import { ADD_TO_CART_GENERIC, CLEAR_CART_ERROR, REMOVE_FROM_CART_GENERIC } from '@/lib/cart/cartErrorMessages';
import useShopifyCart from '@/lib/shopify/cart/useShopifyCart';
import { getInternalApiOptions } from '@/lib/api/utilities';
import { elevarAddToCart, elevarRemoveFromCart } from '@/lib/elevar/cart-events';
import { klaviyoAddedToCart } from '@/lib/klaviyo/events';
import {
  createContext,
  startTransition,
  useCallback,
  useContext,
  useMemo,
  useOptimistic,
  useState,
} from 'react';
import { pricingApiFor, usePricingSettingsPromise } from '@/contexts/PricingContext';
import { useHydrated } from '@/utils/use-hydrated';
const CartContext = createContext(undefined);

// Not awaited by the caller: the cart write already succeeded, so a rejected
// settingsPromise or a dataLayer push failure must not surface as a cart error.
async function fireCartAnalytics(label, settingsPromise, push) {
  try {
    await push(pricingApiFor(await settingsPromise));
  } catch (error) {
    console.error(`[CartContext] ${label} analytics failed:`, error);
  }
}

// Uses native float arithmetic for the optimistic UI state.
// Downstream presentation layers (Intl.NumberFormat) normalize any float noise.
function calculateItemCost(quantity, price) {
  return (Number(price) * quantity).toString();
}

function createEmptyCart() {
  return {
    id: undefined,
    checkoutUrl: '',
    totalQuantity: 0,
    lines: [],
    cost: {
      subtotalAmount: { amount: '0', currencyCode: 'USD' },
      totalAmount: { amount: '0', currencyCode: 'USD' },
      totalTaxAmount: { amount: '0', currencyCode: 'USD' },
    },
  };
}

function createOrUpdateCartItem(
  existingItem,
  variant,
  product,
  quantity = 1,
  attributes = [],
) {
  const newQuantity = existingItem ? existingItem.quantity + quantity : quantity;

  const priceObj = variant?.price || { amount: '0', currencyCode: 'USD' };
  const compareAtPriceObj = variant?.compareAtPrice || priceObj;
  const totalAmount = calculateItemCost(newQuantity, priceObj.amount);

  return {
    // Assign a stable temp ID so the DELETE reducer and React keys always have
    // a non-undefined value during the optimistic pending window. Replaced by
    // the real Shopify line ID once setCart(newCart) resolves.
    id: existingItem?.id ?? `optimistic-${crypto.randomUUID()}`,
    quantity: newQuantity,
    attributes: existingItem?.attributes ?? attributes,
    cost: {
      totalAmount: {
        amount: totalAmount,
        currencyCode: priceObj.currencyCode,
      },
    },
    merchandise: {
      id: variant?.id,
      title: variant?.title,
      selectedOptions: variant?.selectedOptions || [],
      compareAtPrice: {
        amount: compareAtPriceObj?.amount || priceObj.amount,
        currencyCode:
          compareAtPriceObj?.currencyCode || priceObj.currencyCode,
      },
      price: {
        amount: priceObj.amount,
        currencyCode: priceObj.currencyCode,
      },
      product: {
        id: product?.id,
        availableForSale: product?.availableForSale,
        handle: product?.handle,
        title: product?.title,
        featuredImage: product?.featuredImage,
        productType: product?.productType,
      },
      sku: variant?.sku,
    },
  };
}

function updateCartTotals(lines) {
  const totalQuantity = lines.reduce((sum, item) => sum + item.quantity, 0);
  const totalAmount = lines.reduce(
    (sum, item) => sum + Number(item?.cost?.totalAmount?.amount || 0),
    0,
  );
  const currencyCode = lines[0]?.cost?.totalAmount?.currencyCode ?? 'USD';

  return {
    totalQuantity,
    cost: {
      subtotalAmount: { amount: totalAmount.toString(), currencyCode },
      totalAmount: { amount: totalAmount.toString(), currencyCode },
      totalTaxAmount: { amount: '0', currencyCode },
    },
  };
}

function cartReducer(state, action) {
  const currentCart = state || createEmptyCart();

  try {
    switch (action.type) {
      case 'DELETE': {
        const { lineItemIds } = action.payload;
        const updatedLines = currentCart.lines.filter(
          (item) => !lineItemIds.includes(String(item.id)),
        );

        return {
          ...currentCart,
          ...updateCartTotals(updatedLines),
          lines: updatedLines,
        };
      }

      case 'ADD_MULTIPLE_ITEMS': {
        const items = action.payload;

        const updatedLines = items.reduce(
          (lines, {
            variant,
            product,
            productData,
            quantity,
            customAttributes = [],
          }) => {
            const normalizedProduct =
              product ?? productData?.shopifyProduct ?? productData;

            const existingItem = lines.find(
              (item) =>
                item.merchandise.id === variant.id &&
                !item.attributes?.some((attr) => attr.key === '_split_id'),
            );

            const updatedItem = createOrUpdateCartItem(
              existingItem,
              variant,
              normalizedProduct,
              quantity ?? 1,
              customAttributes,
            );

            if (existingItem) {
              return lines.map((item) =>
                item === existingItem ? updatedItem : item,
              );
            }

            return [...lines, updatedItem];
          },
          currentCart.lines || [],
        );

        return {
          ...currentCart,
          ...updateCartTotals(updatedLines),
          lines: updatedLines,
        };
      }

      default:
        return currentCart;
    }
  } catch (error) {
    console.error('[CartContext] cartReducer error:', error);
    return currentCart;
  }
}

async function parseCartApiResponse(res, endpointName = 'API') {
  const text = await res.text();

  let data;
  try {
    data = JSON.parse(text);
  } catch (parseError) {
    console.error(`[CartContext] Invalid response from ${endpointName}:`, text, parseError);
    throw new Error('The API did not return valid JSON.');
  }

  if (!res.ok) {
    console.error(`[CartContext] ${endpointName} non-OK response:`, res.status, data);
    throw new Error(`[${endpointName}] HTTP ${res.status}`);
  }

  return data;
}

export function CartProvider({ children }) {
  const [cart, setCart] = useShopifyCart();
  const settingsPromise = usePricingSettingsPromise();
  // Tracks which button action ('plus' | 'minus' | null) is in-flight.
  // Managed internally by deleteCartItem/addCartItems — not exposed as a setter.
  const [cartLoadingAction, setCartLoadingAction] = useState(null);
  const [isClearingCart, setIsClearingCart] = useState(false);
  const [clearCartError, setClearCartError] = useState(null);

  const [optimisticCart, updateOptimisticCart] = useOptimistic(
    cart,
    cartReducer,
  );

  // Deletes item optimistically
  const deleteCartItem = useCallback(
    async (lineItemIds, source = 'cart') => {
      if (!cart?.id || !lineItemIds?.length) {
        console.error(
          '[CartContext] deleteCartItem validation failed:',
          !cart?.id ? 'missing cart.id' : 'missing lineItemIds',
        );

        return {
          cart: null,
          error: {
            message: REMOVE_FROM_CART_GENERIC,
          },
        };
      }

      // Capture products before they are removed
      const productsToRemove = cart.lines.filter((line) =>
        lineItemIds.includes(String(line.id)),
      );

      // Signal the UI that a delete is in progress before the fetch starts.
      setCartLoadingAction('minus');

      const removeFromCartPromise = fetch(
        '/api/shopify/removeFromCart',
        getInternalApiOptions({
          checkoutId: cart.id,
          lineIds: lineItemIds,
        }),
      ).then((res) => parseCartApiResponse(res, '/api/shopify/removeFromCart'));

      try {
        startTransition(async () => {
          updateOptimisticCart({
            type: 'DELETE',
            payload: { lineItemIds },
          });

          const newCart = await removeFromCartPromise;

          // Must nest another startTransition after awaits
          // https://react.dev/reference/react/useTransition#react-doesnt-treat-my-state-update-after-await-as-a-transition
          startTransition(() => {
            setCart(newCart);
          });
        });

        const resolvedCart = await removeFromCartPromise;

        // Fire Elevar remove from cart on successful res.ok response
        void fireCartAnalytics('deleteCartItem', settingsPromise, (pricing) =>
          elevarRemoveFromCart(
            productsToRemove,
            source === 'cart_quantity_controls' ? 1 : undefined,
            pricing,
          ),
        );

        return { cart: resolvedCart, error: null };
      } catch (error) {
        console.error('[CartContext] deleteCartItem failed:', error);

        return {
          cart: null,
          error: {
            message: REMOVE_FROM_CART_GENERIC,
          },
        };
      } finally {
        // Always clear loading state, regardless of success or failure.
        setCartLoadingAction(null);
      }
    },
    // `optimisticCart` is intentionally omitted: deleteCartItem doesn't read
    // it, and including it (it changes on every optimistic update) would hand
    // consumers a new function reference on each cart mutation, re-rendering
    // every cart line's remove button.
    [cart, setCart, updateOptimisticCart, settingsPromise],
  );

  // Clear cart
  const clearCart = useCallback(async () => {
    if (isClearingCart) {
      return { cart: null, error: null };
    }

    if (!cart?.id) {
      const result = {
        cart: null,
        error: {
          message:
            CLEAR_CART_ERROR,
        },
      };

      setClearCartError(result.error.message);

      return result;
    }

    setIsClearingCart(true);
    setClearCartError(null);

    const itemsToDelete = cart.lines.map((line) => line.id);
    const result = await deleteCartItem(itemsToDelete, 'cart_clear');

    // Stored on the provider (not local component state) because CartSummaryClient
    // swaps CartContent for EmptyState the instant the optimistic count hits zero,
    // remounting CartContent before a failed clear's error could otherwise render.
    if (result.error) setClearCartError(result.error.message);
    setIsClearingCart(false);

    return result;
  }, [cart, deleteCartItem, isClearingCart]);

  const addCartItems = useCallback(
    // loadingAction ('plus' | undefined): when provided, drives the cart-wide
    // loading indicator. Callers from the quantity controls pass 'plus';
    // AddToCartButton omits it and manages its own local loading state.
    async (items, source, loadingAction) => {
      if (!cart?.id || !items?.length) {
        console.error('[CartContext] Missing cart.id or items.');

        return {
          cart: null,
          error: {
            message: ADD_TO_CART_GENERIC,
          },
        };
      }

      // Only set global loading if the caller opted in via loadingAction.
      if (loadingAction) setCartLoadingAction(loadingAction);

      try {
        // Built inside the try so a caller passing an unresolved variant
        // (item.variant undefined) falls through to the catch/finally below
        // instead of throwing before cartLoadingAction is ever cleared.
        const itemsToAdd = items.map((item) => ({
          merchandiseId: item.variant.id,
          quantity: item.quantity ?? 1,
          attributes: item.customAttributes || [],
        }));

        const requestOptions = getInternalApiOptions({
          checkoutId: cart.id,
          lineItems: itemsToAdd,
        });

        const addToCartPromise = fetch(
          '/api/shopify/addToCart',
          requestOptions,
        ).then((res) => parseCartApiResponse(res, '/api/shopify/addToCart'));

        startTransition(async () => {
          updateOptimisticCart({
            type: 'ADD_MULTIPLE_ITEMS',
            payload: items,
          });

          const newCart = await addToCartPromise;

          startTransition(() => {
            setCart(newCart);
          });
        });

        // `addToCartPromise` has two intentional consumers: the transition
        // above (which keeps the optimistic cart pending until the real cart
        // resolves) and this await (which produces the value returned to the
        // caller). It is the same settled Promise — no extra network request.
        const resolvedCart = await addToCartPromise;

        // Fire Elevar add to cart on successful res.ok response
        void fireCartAnalytics('addCartItems', settingsPromise, (pricing) =>
          elevarAddToCart(items, pricing),
        );
        void fireCartAnalytics('klaviyoAddedToCart', settingsPromise, (pricing) =>
          klaviyoAddedToCart(items, resolvedCart, pricing),
        );

        return {
          cart: resolvedCart,
          error: null,
        };
      } catch (error) {
        console.error('[CartContext] addCartItems failed:', error);

        return {
          cart: null,
          error: {
            message: ADD_TO_CART_GENERIC,
          },
        };
      } finally {
        // Mirror the guard above — only clear if we set it.
        if (loadingAction) setCartLoadingAction(null);
      }
    },
    [cart, setCart, updateOptimisticCart, settingsPromise],
  );

  const value = useMemo(
    () => ({
      cart: optimisticCart,
      deleteCartItem,
      addCartItems,
      clearCart,
      cartLoadingAction,
      isCartLoading: Boolean(cartLoadingAction),
      isClearingCart,
      clearCartError,
    }),
    [
      optimisticCart,
      deleteCartItem,
      addCartItems,
      clearCart,
      cartLoadingAction,
      isClearingCart,
      clearCartError,
    ],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const context = useContext(CartContext);

  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }

  // Client-only state: the server always renders an empty cart, so consumers
  // must too until they hydrate, or late boundaries mismatch.
  const hydrated = useHydrated();

  return useMemo(
    () => (hydrated ? context : { ...context, cart: null }),
    [context, hydrated],
  );
}